import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  BonDto,
  BonsLexiClient,
  LogistiqueSettings,
  ReceptionsLexiClient,
  TypeIntegrationBackOffice,
  UniteDto
} from "@lexi-clients/lexi";
import notify from "devextreme/ui/notify";
import { lastValueFrom } from "rxjs";
import { NotificationType } from "../../../references/references";
import { MarchandiseLigne } from "../bon-detail.component";

@Component({
    selector: "app-bon-detail-integration-back-office",
    templateUrl: "./bon-detail-integration-back-office.component.html",
    styleUrls: ["./bon-detail-integration-back-office.component.scss"],
    standalone: false
})
export class BonDetailIntegrationBackOfficeComponent implements OnInit {
  @Input() bon: BonDto;

  private _marchandises: MarchandiseLigne[] = [];

  @Input()
  get marchandises(): MarchandiseLigne[] {
    return this._marchandises;
  }
  set marchandises(value: MarchandiseLigne[]) {
    this._marchandises = value || [];
    this.data = {
      ecarts: this._marchandises
        .filter(x => x.ecart !== 0)
        .map(x => {
          // Correction de l'écart pour éviter les erreurs d'arrondi Javascript
          // Si l'écart calculé diffère de l'écart enregistré, on le corrige
          const ecartCorrige = ((x.quantiteReservee * 1000 - x.quantiteInitiale * 1000) / 1000) !== x.ecart 
          ? (x.quantiteReservee * 1000 - x.quantiteInitiale * 1000) / 1000
          : x.ecart;
          x.ecart = ecartCorrige;
          return x;
        }
        ),
      nombreEcartsNegatifs: this._marchandises.filter(x => x.ecart < 0).length,
      nombreEcartsPositifs: this._marchandises.filter(x => x.ecart > 0).length, 
      nombreArticlesNonPrevus: this.bon?.nombreReferencesNonPrevues
    };
  }

  @Input() unites: UniteDto[] = [];

  @Output() successOrFailure = new EventEmitter<void>();
  @Output() annulerClicked = new EventEmitter<void>();

  data: BonDetailIntegrationBackOfficeData;
  isIntegrationEnCours: boolean = false;
  erreursRetourFinalisation: string[] = [];
  finalisationPopupVisible: boolean = false;
  logistiqueSettings: LogistiqueSettings;
  typeMouvement: TypeIntegrationBackOffice = TypeIntegrationBackOffice.inconnu;

  constructor(
    private readonly bonLexiClient: BonsLexiClient,
    private readonly receptionLexiClient: ReceptionsLexiClient,
  ) {}

  ngOnInit() {
    this.initData();
  }

  annuler() {
    this.annulerClicked.emit();
  }

  private async initData() {
    this.typeMouvement = await lastValueFrom(this.bonLexiClient.getTypeIntegrationBackOffice(this.bon.id));
  }

  processIntegrationBackOffice() {
    if (this.typeMouvement === TypeIntegrationBackOffice.receptionFournisseur) {
      this.finaliserReception();
    } else {
      notify(`Intégration BackOffice non implémentée pour le type de mouvement ${this.typeMouvement}`, NotificationType.Error, 3000);
    }
  }

  private async finaliserReception() {
    try {
      this.isIntegrationEnCours = true;
      const result = await lastValueFrom(
        this.receptionLexiClient.finaliserReception(this.bon.id),
      );

      if (result.erreurs.length > 0) {
        this.erreursRetourFinalisation = result.erreurs;
        notify(
          `Erreur lors de l'intégration, consultez les erreurs.`,
          NotificationType.Error,
          3000,
        );
      } else {
        this.successOrFailure.emit();
        notify("Réception finalisée avec succès", NotificationType.Success, 3000);
        this.finalisationPopupVisible = false;
      }
    } catch (err) {
      notify(
        `Une erreur est survenue: ${err.error}`,
        NotificationType.Error,
        3000,
      );
    } finally {
      this.isIntegrationEnCours = false;
    }
  }
}

export interface BonDetailIntegrationBackOfficeData {
  ecarts: MarchandiseLigne[];
  nombreEcartsPositifs: number;
  nombreEcartsNegatifs: number;
  nombreArticlesNonPrevus: number;
}
